import { useCallback } from 'react'

import {
  type InfiniteData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import {
  type SharePointSite,
  sharepointSubsitesQueryKeys,
} from '@/hooks/sharepoint/types'
import type { FetchSharepointSitesResponse } from '@/hooks/sharepoint/useSharepointSites'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'

export const DEFAULT_SUBSITE_LIMIT = 50

interface UseSharepointSubsitesArgs<T> {
  enabled?: boolean
  orgId: UUID | ''
  siteId: string
  select?: (data: FetchSharepointSitesResponse) => T
}

interface FetchSharepointSubsitesOptions {
  cursor?: string | null
  limit?: number
}

interface UseInfiniteSharepointSubsitesArgs<
  T = InfiniteData<FetchSharepointSitesResponse>,
> {
  enabled?: boolean
  limit?: number
  orgId: UUID | ''
  siteId: string
  select?: (data: InfiniteData<FetchSharepointSitesResponse>) => T
}

export const fetchSharepointSubsites = async (
  orgId: UUID | '',
  siteId: string,
  accessToken: string,
  options: FetchSharepointSubsitesOptions = {},
): Promise<FetchSharepointSitesResponse> => {
  if (!orgId) throw new Error('No org ID provided.')
  if (!siteId) throw new Error('No site ID provided.')
  const params = new URLSearchParams()
  if (options.limit) params.set('limit', String(options.limit))
  if (options.cursor) params.set('cursor', options.cursor)
  const query = params.toString()
  const url = `${API_URL}/api/v1/organizations/${orgId}/sharepoint/sites/${encodeURIComponent(siteId)}/subsites${query ? `?${query}` : ''}`
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }
  return (await res.json()) as FetchSharepointSitesResponse
}

export const useSharepointSubsites = <T = SharePointSite[]>({
  enabled = true,
  orgId,
  siteId,
  select,
}: UseSharepointSubsitesArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: FetchSharepointSitesResponse): T => {
      return select ? select(data) : (data.data as unknown as T)
    },
    [select],
  )

  return useQuery({
    queryKey: sharepointSubsitesQueryKeys.site({ orgId, siteId }),
    queryFn: () => fetchSharepointSubsites(orgId, siteId, accessToken),
    enabled: !!orgId && !!siteId && enabled,
    staleTime: Infinity,
    select: selectFn,
  })
}

export const useInfiniteSharepointSubsites = <T = SharePointSite[]>({
  enabled = true,
  limit = DEFAULT_SUBSITE_LIMIT,
  orgId,
  siteId,
  select,
}: UseInfiniteSharepointSubsitesArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: InfiniteData<FetchSharepointSitesResponse>): T => {
      if (select) {
        return select(data)
      }
      return data.pages.flatMap((page) => page.data) as unknown as T
    },
    [select],
  )

  return useInfiniteQuery({
    queryKey: sharepointSubsitesQueryKeys.paginatedSite({
      orgId,
      siteId,
      limit,
    }),
    queryFn: ({ pageParam }) =>
      fetchSharepointSubsites(orgId, siteId, accessToken, {
        cursor: pageParam ?? null,
        limit,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_cursor : undefined,
    enabled: !!orgId && !!siteId && enabled,
    staleTime: Infinity,
    select: selectFn,
  })
}
